import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import {
  prettyPrintAmountCode,
  prettyPrintQuantity,
} from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import { ChemicalsWithMeasurementsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatChemicalName } from "util/chemicalName";

import { useFacility } from "../Facility/useFacility";
import { ChemicalsTableProps } from "./ChemicalsTable";
import { DeleteChemicalMeasurementsConfirmDialog } from "./DeleteChemicalMeasurementsConfirmDialog";
import { FacilityChemicalMeasurementForm } from "./FacilityChemicalMeasurementForm";
import { portalEhsColumns } from "./portalEhsColumn";
import { GET_REPORT, useIgnoredIssues } from "./Report/useReport";

type Row = ChemicalsWithMeasurementsQuery["facilityChemicalMeasurementsForReport"]["items"][number];

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];
const EXCLUDE_FILTER_COLUMNS = ["issues", "facilityChemicalMeasurementCount", "maxAmount"];
const getItems = (data: ChemicalsWithMeasurementsQuery) =>
  data.facilityChemicalMeasurementsForReport.items;
const getCount = (data: ChemicalsWithMeasurementsQuery) =>
  data.facilityChemicalMeasurementsForReport.count;

export const CHEMICALS_WITH_MEASUREMENTS = gql(`
  query ChemicalsWithMeasurements($facilityId: ID!, $reportingYear: Int!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilityChemicalMeasurementsForReport(facilityId: $facilityId, reportingYear: $reportingYear, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        pureOrMixture
        casNumber
        isEhs
        portalIsEhs
        components {
          name
          casNumber
          componentPercentage
          isEhs
        }
        alternateId
        maxAmount {
          amount
          amountCode
          unit
        }
        totalMeasurements
        hasCarryOver
        issues {
          ...issue
        }
      }
      count
    }
  }
`);

const DELETE_FACILITY_CHEMICAL_MEASUREMENTS_BY_CHEMICAL_AND_YEAR = gql(`
  mutation DeleteFacilityChemicalMeasurementsByChemicalAndYear($facilityId: ID!, $chemicalId: ID!, $reportingYear: Int!, $addZeroMeasurement: Boolean) {
    deleteFacilityChemicalMeasurementsByChemicalAndYear(facilityId: $facilityId, chemicalId: $chemicalId, reportingYear: $reportingYear, addZeroMeasurement: $addZeroMeasurement) {
      facilityId
      chemicalId
      reportingYear
    }
  }
`);

function useChemicalsWithMeasurementsColumns({
  facilityState,
  issuesToIgnore,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  facilityState: string | null | undefined;
  issuesToIgnore: ReturnType<typeof useIgnoredIssues>;
  onEdit: (chemicalId: string) => void;
  onDelete: (row: Row) => void;
  isReadOnly: boolean;
}) {
  return useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        renderCell: ({ row }) => (
          <TextCellWithTooltip tooltip={formatChemicalName(row)}>{row.name}</TextCellWithTooltip>
        ),
      },
      {
        field: "casNumber",
        headerName: "CAS #",
        flex: 1,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.casNumber}</TextCellWithTooltip>,
      },
      {
        field: "alternateId",
        headerName: "Alt ID",
        flex: 1,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.alternateId}</TextCellWithTooltip>,
      },
      {
        field: "isEhs",
        headerName: "Is EHS",
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        filterKeyType: "boolean",
        renderCell: ({ row }) => (row.isEhs ? <Check /> : <></>),
      },
      ...portalEhsColumns<Row>(facilityState, (row) => row),
      {
        field: "maxAmount",
        headerName: "Max Amt",
        valueGetter: (_, row) => {
          if (row.maxAmount?.amount) {
            return prettyPrintQuantity({
              amount: row.maxAmount.amount,
              unit: row.maxAmount.unit,
              state: facilityState,
            });
          } else if (row.maxAmount?.amountCode != null) {
            return prettyPrintAmountCode(
              facilityState,
              row.maxAmount?.amountCode,
              row.maxAmount?.unit,
              true
            );
          }
          return "";
        },
        flex: 1.3,
        sortable: false,
        renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
      },
      {
        field: "facilityChemicalMeasurementCount",
        headerName: "Measurements",
        valueGetter: (_, row) => row.totalMeasurements,
        flex: 0.8,
      },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.5,
        renderCell: ({ row: { issues } }) => {
          const shouldDimIssues = issues?.some((issue) =>
            issuesToIgnore.some((ignoredIssue) => ignoredIssue.modelId === issue.modelId)
          );
          return <IssueCount issueCount={issues?.length} ignoreForReport={shouldDimIssues} />;
        },
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.6,
        align: "right",
        getActions: ({ row }) => {
          return [
            <GridActionsCellItem
              key="edit"
              label="Edit"
              icon={<Edit />}
              onClick={() => onEdit(row.id)}
            />,
            <GridActionsCellItem
              key="delete"
              label="Delete"
              icon={<Delete />}
              onClick={() => onDelete(row)}
              disabled={isReadOnly}
            />,
          ];
        },
      },
    ],
    [facilityState, issuesToIgnore, onEdit, onDelete, isReadOnly]
  );
}

export function ChemicalsWithMeasurementsTable({ facilityId, reportId }: ChemicalsTableProps) {
  const issuesToIgnore = useIgnoredIssues();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { navigateWithFilters } = useQueryParams<{
    ry: string;
    facilityId: string;
    omnisearch?: string;
  }>();
  const reportingYear: number = useSelectedReportingYear();
  const { data: facilityQuery } = useFacility(facilityId);
  const facilityState = facilityQuery?.facility?.state;
  const { isReadOnly } = useFacilityReadOnly(facilityId);

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const defaults = `facilityId:${facilityId} reportingYear:${reportingYear}`;
      return {
        facilityId,
        reportingYear,
        search: omnisearch ? `${omnisearch} ${defaults}` : defaults,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [facilityId, reportingYear]
  );

  const [showAddChemical, setShowAddChemical] = useState(false);
  const [deletingChemicalMeasurements, setDeletingChemicalMeasurements] = useState<
    Row | undefined
  >();

  const location = useLocation();
  const usedInInventory = location.pathname.includes("inventory");

  const navigateToChemicalMeasurements = useCallback(
    (chemicalId: string) => {
      const destination = `measurements/${chemicalId}`;
      if (usedInInventory) {
        navigateWithFilters(destination, (filters) => {
          filters.facilityId = facilityId;
          filters.ry = String(reportingYear);
        });
        return;
      }

      navigate(destination);
    },
    [facilityId, navigate, navigateWithFilters, usedInInventory, reportingYear]
  );

  const [deleteFacilityChemicalMeasurementsByChemicalAndYear, { loading: isDeleting }] =
    useMutation(DELETE_FACILITY_CHEMICAL_MEASUREMENTS_BY_CHEMICAL_AND_YEAR, {
      onCompleted() {
        alerts.success("Successfully deleted the measurements");
        setDeletingChemicalMeasurements(undefined);
      },
      onError(err) {
        alerts.error("An error occurred deleting the measurements", err);
        setDeletingChemicalMeasurements(undefined);
      },
      refetchQueries: [CHEMICALS_WITH_MEASUREMENTS, GET_REPORT],
    });

  const handleDelete = useCallback(
    async (chemicalId: string, addZeroMeasurement?: boolean) => {
      if (!deletingChemicalMeasurements) return;
      await deleteFacilityChemicalMeasurementsByChemicalAndYear({
        variables: {
          facilityId,
          chemicalId: chemicalId,
          reportingYear,
          addZeroMeasurement,
        },
      });
    },
    [
      deleteFacilityChemicalMeasurementsByChemicalAndYear,
      deletingChemicalMeasurements,
      facilityId,
      reportingYear,
    ]
  );

  const handleAddChemical = useCallback(() => {
    setShowAddChemical(true);
  }, []);

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => navigateToChemicalMeasurements(params.row.id),
    [navigateToChemicalMeasurements]
  );

  const columns = useChemicalsWithMeasurementsColumns({
    facilityState,
    issuesToIgnore,
    onEdit: navigateToChemicalMeasurements,
    onDelete: setDeletingChemicalMeasurements,
    isReadOnly,
  });

  const commandButtons = useMemo(
    () => [
      !reportId ? (
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            size: "small",
          }}
          persistToLocalStorage
        />
      ) : undefined,
      <CommandButton
        key="add"
        variant="contained"
        onClick={handleAddChemical}
        disabled={isReadOnly}
      >
        Add Chemical
      </CommandButton>,
    ],
    [reportId, handleAddChemical, isReadOnly]
  );

  return (
    <>
      <OmnisearchDataGridV2
        persistenceKey="chemicals-with-measurements"
        columns={columns}
        query={CHEMICALS_WITH_MEASUREMENTS}
        buildVariables={buildVariables}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={25}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        getCount={getCount}
        getItems={getItems}
        compact
        commandButtons={commandButtons}
        noRowsMessage="No chemical information has been added to this facility yet."
        noDataButtonText={isReadOnly ? undefined : "Add Chemical"}
        onNoDataButtonClick={isReadOnly ? undefined : handleAddChemical}
        onRowClick={handleRowClick}
      />
      <DeleteChemicalMeasurementsConfirmDialog
        measurementToDelete={deletingChemicalMeasurements}
        setDeletingChemicalMeasurements={setDeletingChemicalMeasurements}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
        reportingYear={reportingYear}
      />
      {showAddChemical && (
        <FacilityChemicalMeasurementForm
          facilityId={facilityId}
          onClose={() => setShowAddChemical(false)}
          open={showAddChemical}
        />
      )}
    </>
  );
}
